import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import GuestPortalFooter from "../components/GuestPortalFooter";
import { setAgentThemeColor } from "../utils/agentTheme";
import { BACKEND_URL } from "../config/api";

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [agentContext, setAgentContext] = useState(null);

  useEffect(() => {
    const bookingData = sessionStorage.getItem("bookingData");
    if (!bookingData) {
      navigate("/");
      return;
    }
    const parsed = JSON.parse(bookingData);
    setBooking(parsed);
    
    const agentData = sessionStorage.getItem("agentContext");
    if (agentData) {
      const parsedAgent = JSON.parse(agentData);
      setAgentContext(parsedAgent);
      setAgentThemeColor(parsedAgent.button_color || parsed.button_color);
    } else {
      setAgentThemeColor(parsed.button_color);
    }
  }, [navigate]);

  const agentName = agentContext?.agent_name || booking?.agent_name || "Charles Marlow";
  
  const getLogoUrl = () => {
    const logoUrl = agentContext?.logo_url || booking?.logo_url;
    if (!logoUrl) return null;
    if (logoUrl.startsWith('/')) return `${BACKEND_URL}${logoUrl}`;
    return logoUrl;
  };

  // Get display name for a selection, filtering out empty entries
  const getSelectionName = (sel) => {
    return sel.service_name || sel.experience_name || sel.yacht_name || "";
  };

  const validSelections = (booking?.selections || []).filter(sel => getSelectionName(sel));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with Logo */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container-luxury py-8 flex items-center justify-center">
          {getLogoUrl() ? (
            <img src={getLogoUrl()} alt={agentName} className="h-14 md:h-16 object-contain" style={{ maxWidth: '200px' }} />
          ) : (
            <span className="brand-logo brand-logo-small">{agentName}</span>
          )}
        </div>
      </div>

      <div className="container-luxury pt-36 pb-20 flex-grow">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-md mx-auto">
          {/* Success icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 150 }}
            className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mx-auto mb-10"
          >
            <Check className="w-7 h-7 text-white" />
          </motion.div>

          <h1 className="heading-display text-2xl md:text-3xl mb-8 text-center" data-testid="confirmation-heading">
            Your concierge plan has been submitted
          </h1>
          
          <div className="bg-secondary/30 rounded-xl p-6 md:p-8 border border-border/30 mb-6 text-left">
            <p className="text-base text-muted-foreground leading-relaxed mb-4" style={{ textWrap: 'pretty' }}>
              Thank you. We are already looking into your selections and will be in touch shortly to confirm your arrangements.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed" style={{ textWrap: 'pretty' }}>
              We look forward to making your stay truly special.
            </p>

            {booking?.reference_number && (
              <p className="text-sm text-muted-foreground border-t border-border/30 pt-4 mt-4">
                Booking reference: <strong>{booking.reference_number}</strong>
              </p>
            )}
          </div>

          {/* Selections summary */}
          {validSelections.length > 0 && (
            <div className="bg-secondary/20 rounded-xl p-5 border border-border/20 mb-6 text-left">
              <p className="text-xs uppercase tracking-widest text-muted-foreground/60 font-medium mb-3">Your selections</p>
              <div className="space-y-2">
                {validSelections.map((sel, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {getSelectionName(sel)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Card capture footnote */}
          <p className="text-left leading-relaxed mb-8 px-1" style={{ fontSize: '11px', color: '#a09a93', textWrap: 'pretty' }}>
            To help us secure your reservations and bookings, once your services are agreed you will receive a separate email with a secure link to save your card details. No payment is ever taken — your card is held on file only to pre-authorise specific bookings such as restaurant deposits or beach club minimum spends. You will always be notified before any charge is made.
          </p>

          <div className="text-center space-y-3">
            <button
              onClick={() => {
                // Navigate back to services — sessionStorage still has booking data
                navigate("/services");
              }}
              data-testid="add-more-btn"
              className="btn-agent-primary w-full"
            >
              Add More to My Plan
            </button>
            <button
              onClick={() => {
                const agentSlug = agentContext?.agent_slug || booking?.agent_slug || "charles-marlow";
                sessionStorage.clear();
                navigate(`/a/${agentSlug}`);
              }}
              data-testid="return-home-btn"
              className="w-full py-3 text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
            >
              Return to Home
            </button>
          </div>
        </motion.div>
      </div>
      
      <GuestPortalFooter 
        poweredByBrand={agentContext?.powered_by_brand || booking?.powered_by_brand || "my_concierge_portal"}
        privacyPolicyUrl={agentContext?.privacy_policy_url || booking?.privacy_policy_url || "https://myworldvip.com/privacy-policy"}
        termsConditionsUrl={agentContext?.terms_conditions_url || booking?.terms_conditions_url || "https://myworldvip.com/terms-and-conditions"}
      />
    </div>
  );
}
